import type { GridMouseEventArgs, Item } from '@glideapps/glide-data-grid'
import useDebounce from '@hooks/useDebounce'
import { useCallback, useRef } from 'react'

import type { AvailableColumn, RowMarkerOptions } from '../types'
import { preventRowClickOnSelectionCheckbox } from '../utils'

const DEBOUNCE_TIME = 100

export const getCellAction = (
  availableColumns: readonly AvailableColumn[] | undefined,
  column: number,
) => availableColumns?.[column]?.action

export const useRowAnchor = ({
  getRowAnchorUrl,
  rowMarkers,
  availableColumns,
}: {
  getRowAnchorUrl?: (item: Item) => string
  rowMarkers: RowMarkerOptions['kind']
  availableColumns?: readonly AvailableColumn[]
}) => {
  const rowAnchorRef = useRef<HTMLAnchorElement | null>(null)

  const setRowAnchorRef = useCallback((element: HTMLAnchorElement) => {
    rowAnchorRef.current = element
  }, [])

  const debouncedSetPosition = useDebounce((args: GridMouseEventArgs) => {
    if (args.kind !== 'cell' || !rowAnchorRef.current || !getRowAnchorUrl) {
      return
    }

    const action = getCellAction(availableColumns, args.location[0])
    const href = getRowAnchorUrl(args.location)

    if (!href || action) {
      return
    }

    if (preventRowClickOnSelectionCheckbox(rowMarkers, args.location[0])) {
      return
    }

    rowAnchorRef.current.style.left = `${window.scrollX + args.bounds.x}px`
    rowAnchorRef.current.style.width = `${args.bounds.width}px`
    rowAnchorRef.current.style.top = `${window.scrollY + args.bounds.y}px`
    rowAnchorRef.current.style.height = `${args.bounds.height}px`
    rowAnchorRef.current.href = href.startsWith('/') ? href : `/${href}`
    rowAnchorRef.current.dataset.reactRouterPath = href
  }, DEBOUNCE_TIME)

  const setAnchorPosition = useCallback(
    (args: GridMouseEventArgs) => {
      debouncedSetPosition(args)
    },
    [debouncedSetPosition],
  )

  return {
    rowAnchorRef,
    setAnchorPosition,
    setRowAnchorRef,
  }
}
